import * as THREE from 'three';
import { TWEEN } from '/node_modules/three/examples/jsm/libs/tween.module.min.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader';
import { Water } from 'three/addons/objects/Water.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { GammaCorrectionShader } from 'three/addons/shaders/GammaCorrectionShader.js';
import SCENE_CONFIG from './configs/scene-config';
import MainScene from '../main-scene';
import LoadingOverlay from './loading-overlay';
import { CanvasDriver, Engine, Input, MasterAudio, StageScaleMode } from 'black-engine';
import Loader from './loader';
import Scene3DDebugMenu from './helpers/gui-helper/scene-3d-debug-menu';
import { CAMERA_CONFIG } from '../scene/room/camera-controller/data/camera-config';
import MONITOR_SCREEN_SCENE_CONFIG from './configs/monitor-screen-scene-config';
import DEBUG_CONFIG from './configs/debug-config';
import Materials from './materials';
import WebGL from 'three/addons/capabilities/WebGL.js';
import { GLOBAL_LIGHT_CONFIG } from './configs/global-light-config';
import isMobile from 'ismobilejs';
import { THEATRE_JS_CONFIG } from '../scene/room/intro/theatre-js/data/theatre-js-config';


export default class BaseScene {
  constructor() {
    this._scene = null;
    this._renderer = null;
    this._camera = null;
    this._loadingOverlay = null;
    this._mainScene = null;
    this._scene3DDebugMenu = null;
    this._effectComposer = null;
    this._orbitControls = null;
    this._audioListener = null;
    this._monitorScreenSceneData = null;
    this._renderPass = null;

    this._windowSizes = {};
    this._isAssetsLoaded = false;
    this._isGameActive = false;
    this._isSecondaryCameraActive = false;

    SCENE_CONFIG.isMobile = isMobile(window.navigator).any;

    this._init();
  }

  createGameScene() {
    this._initMaterials();

    const data = {
      scene: this._scene,
      camera: this._camera,
      renderer: this._renderer,
      orbitControls: this._orbitControls,
      audioListener: this._audioListener,
      monitorScreenData: this._monitorScreenSceneData,
    };

    this._mainScene = new MainScene(data);

    this._initMainSceneSignals();
  }

  afterAssetsLoaded() {
    this._isAssetsLoaded = true;

    this._loadingOverlay.hide();
    this._scene3DDebugMenu.showAfterAssetsLoad();
    this._mainScene.afterAssetsLoad();
    this._setupBackgroundColor();
  }

  _initMainSceneSignals() {
    this._mainScene.events.on('fpsMeterChanged', () => this._scene3DDebugMenu.onFpsMeterClick());
    this._mainScene.events.on('onShowGame', () => this._onShowGame());
    this._mainScene.events.on('onHideGame', () => this._onHideGame());
    this._mainScene.events.on('onSwitchToReserveCamera', () => this._onSwitchToReserveCamera());
  }

  _onSwitchToReserveCamera() {
    if (this._isSecondaryCameraActive) {
      this._isSecondaryCameraActive = false;
      this._renderPass.camera = this._camera;
    } else {
      this._isSecondaryCameraActive = true;
      this._renderPass.camera = this._reserveCamera;
    }

    this._onResize();
  }

  _init() {
    this._checkDeploymentConfig();
    this._initBlack();
    this._initThreeJS();
    this._initUpdate();
  }

  _checkDeploymentConfig() {
    DEBUG_CONFIG.skipIntro = true
    if (process.env.NODE_ENV === 'development') {
      DEBUG_CONFIG.fpsMeter = true;
      DEBUG_CONFIG.skipIntro = true;
    }

    if (THEATRE_JS_CONFIG.studioEnabled) {
      DEBUG_CONFIG.fpsMeter = false;
      SCENE_CONFIG.fxaaPass = false;
      CAMERA_CONFIG.far = 500;

      if (THEATRE_JS_CONFIG.studioEnabled) {
        this._isSecondaryCameraActive = true;
      }
    }
  }

  _initBlack() {
    const engine = new Engine('container', Loader, CanvasDriver, [Input, MasterAudio]);

    engine.pauseOnBlur = false;
    engine.pauseOnHide = false;
    engine.start();

    engine.stage.setSize(640, 960);
    engine.stage.scaleMode = StageScaleMode.LETTERBOX;
  }

  _initThreeJS() {
    this._initScene();
    this._initRenderer();
    this._initCamera();
    this._initLights();
    this._initLoadingOverlay();
    this._initOnResize();
    this._initPostProcessing();
    this._initAudioListener();
    this._initMonitorScreenScene();
    this._ocean = this._initOcean();
    this._initRaft();
    this._addBretonFlag();
    this._initPoineDuRaz();

    this._initScene3DDebugMenu();
  }

  _initScene() {
    this._scene = new THREE.Scene();
  }

  _initRenderer() {
    this._windowSizes = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const canvas = document.querySelector('canvas.webgl');

    const renderer = this._renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: SCENE_CONFIG.antialias,
      powerPreference: 'high-performance',
    });

    renderer.setSize(this._windowSizes.width, this._windowSizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, SCENE_CONFIG.maxPixelRatio));

    renderer.useLegacyLights = false;

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
  }

  _initCamera() {
    const camera = this._camera = new THREE.PerspectiveCamera(CAMERA_CONFIG.fov, this._windowSizes.width / this._windowSizes.height, CAMERA_CONFIG.near, CAMERA_CONFIG.far);
    this._scene.add(camera);

    if (THEATRE_JS_CONFIG.studioEnabled) {
      const helper = new THREE.CameraHelper(camera);
      this._scene.add(helper);

      const reserveCamera = this._reserveCamera = new THREE.PerspectiveCamera(CAMERA_CONFIG.fov, this._windowSizes.width / this._windowSizes.height, CAMERA_CONFIG.near, CAMERA_CONFIG.far);
      this._scene.add(reserveCamera);
    }
  }

  _initLights() {
    if (GLOBAL_LIGHT_CONFIG.ambient.enabled) {
      const ambientLight = new THREE.AmbientLight(GLOBAL_LIGHT_CONFIG.ambient.color, GLOBAL_LIGHT_CONFIG.ambient.intensity);
      this._scene.add(ambientLight);
    }
  
    const sunLight = new THREE.DirectionalLight(0xffffff, 1.0);
    sunLight.position.set(0, 100, 100);
    sunLight.castShadow = true;
    
    sunLight.shadow.mapSize.width = 512;
    sunLight.shadow.mapSize.height = 512;
    sunLight.shadow.camera.near = 0.5;
    sunLight.shadow.camera.far = 500;
  
    this._scene.add(sunLight);
  
    sunLight.shadow.camera.left = -50;
    sunLight.shadow.camera.right = 50;
    sunLight.shadow.camera.top = 50;
    sunLight.shadow.camera.bottom = -50;
  }
  

  _initMaterials() {
    new Materials();
  }

  _initLoadingOverlay() {
    const loadingOverlay = this._loadingOverlay = new LoadingOverlay();
    this._scene.add(loadingOverlay);
  }

  _initOnResize() {
    window.addEventListener('resize', () => this._onResize());
  }

  _onResize() {
    this._windowSizes.width = window.innerWidth;
    this._windowSizes.height = window.innerHeight;
    const pixelRatio = Math.min(window.devicePixelRatio, SCENE_CONFIG.maxPixelRatio);

    this._camera.aspect = this._windowSizes.width / this._windowSizes.height;
    this._camera.updateProjectionMatrix();

    if (THEATRE_JS_CONFIG.studioEnabled) {
      this._reserveCamera.aspect = this._windowSizes.width / this._windowSizes.height;
      this._reserveCamera.updateProjectionMatrix();
    }

    this._renderer.setSize(this._windowSizes.width, this._windowSizes.height);
    this._renderer.setPixelRatio(pixelRatio);

    if (this._effectComposer) {
      this._effectComposer.setSize(this._windowSizes.width, this._windowSizes.height);
      this._effectComposer.setPixelRatio(pixelRatio);
    }

    if (SCENE_CONFIG.fxaaPass) {
      this._fxaaPass.material.uniforms['resolution'].value.x = 1 / (this._windowSizes.width * pixelRatio);
      this._fxaaPass.material.uniforms['resolution'].value.y = 1 / (this._windowSizes.height * pixelRatio);
    }
  }

  _setupBackgroundColor() {
    const texture = Loader.assets['environment'];
    const renderTarget = new THREE.WebGLCubeRenderTarget(texture.image.height);
    renderTarget.fromEquirectangularTexture(this._renderer, texture);
    this._scene.background = renderTarget.texture;
  }

  _initPostProcessing() {
    if (SCENE_CONFIG.isMobile) {
      return;
    }

    this._initEffectsComposer();
    this._initAntiAliasingPass();
    this._initGammaCorrectionPass();
  }

  _initEffectsComposer() {
    const pixelRatio = Math.min(window.devicePixelRatio, SCENE_CONFIG.maxPixelRatio);

    if (WebGL.isWebGL2Available() && pixelRatio === 1) {
      const size = this._renderer.getDrawingBufferSize(new THREE.Vector2());
      const target = new THREE.WebGLRenderTarget(size.width, size.height, { samples: 2 });
      this._effectComposer = new EffectComposer(this._renderer, target);
    } else {
      SCENE_CONFIG.fxaaPass = true;
      this._effectComposer = new EffectComposer(this._renderer);
    }

    const camera = THEATRE_JS_CONFIG.studioEnabled ? this._reserveCamera : this._camera;
    const renderPass = this._renderPass = new RenderPass(this._scene, camera);
    this._effectComposer.addPass(renderPass);
  }
  _initAntiAliasingPass() {
    if (SCENE_CONFIG.fxaaPass) {
      const fxaaPass = this._fxaaPass = new ShaderPass(FXAAShader);
      this._effectComposer.addPass(fxaaPass);

      const pixelRatio = Math.min(window.devicePixelRatio, SCENE_CONFIG.maxPixelRatio);
      fxaaPass.material.uniforms['resolution'].value.x = 1 / (this._windowSizes.width * pixelRatio);
      fxaaPass.material.uniforms['resolution'].value.y = 1 / (this._windowSizes.height * pixelRatio);
    }
  }

  _initGammaCorrectionPass() {
    if (SCENE_CONFIG.gammaCorrectionPass) {
      const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
      this._effectComposer.addPass(gammaCorrectionPass);
    }
  }

  _initAudioListener() {
    const camera = THEATRE_JS_CONFIG.studioEnabled ? this._reserveCamera : this._camera;
    const audioListener = this._audioListener = new THREE.AudioListener();
    camera.add(audioListener);
  }

  _initScene3DDebugMenu() {
    const camera = THEATRE_JS_CONFIG.studioEnabled ? this._reserveCamera : this._camera;
    this._scene3DDebugMenu = new Scene3DDebugMenu(this._scene, camera, this._renderer);
    this._orbitControls = this._scene3DDebugMenu.getOrbitControls();
  }

  _initMonitorScreenScene() {
    this._monitorScreenScene = new THREE.Scene();

    const near = MONITOR_SCREEN_SCENE_CONFIG.camera.near;
    const far = MONITOR_SCREEN_SCENE_CONFIG.camera.far;
    this._monitorScreenCamera = new THREE.PerspectiveCamera(MONITOR_SCREEN_SCENE_CONFIG.camera.fov, MONITOR_SCREEN_SCENE_CONFIG.camera.aspect, near, far);
    this._monitorScreenRenderTarget = new THREE.WebGLRenderTarget(MONITOR_SCREEN_SCENE_CONFIG.renderTarget.width, MONITOR_SCREEN_SCENE_CONFIG.renderTarget.height);

    this._monitorScreenSceneData = {
      scene: this._monitorScreenScene,
      camera: this._monitorScreenCamera,
      renderTarget: this._monitorScreenRenderTarget,
      audioListener: this._audioListener,
    };
  }

  _initUpdate() {
    const clock = new THREE.Clock(true);

    const update = () => {
      this._scene3DDebugMenu.preUpdate();

      const deltaTime = clock.getDelta();

      if (this._isAssetsLoaded) {
        TWEEN.update();
        this._scene3DDebugMenu.update();

        if (this._mainScene) {
            this._mainScene.update(deltaTime);
        }

        if (this._water) {
            this._water.material.uniforms['time'].value += deltaTime;
        }
        
        if (this._flagMaterial) {
          this._flagMaterial.uniforms.time.value += deltaTime;
        }

        if (this._isGameActive) {
            this._renderer.setRenderTarget(this._monitorScreenRenderTarget);
            this._renderer.clear();
            this._renderer.render(this._monitorScreenScene, this._monitorScreenCamera);
        }

        if (SCENE_CONFIG.isMobile || DEBUG_CONFIG.rendererStats) {
            this._renderer.setRenderTarget(null);
            this._renderer.clear();
            this._renderer.render(this._scene, this._camera);
        } else {
            this._effectComposer.render();
        }
      }

      this._scene3DDebugMenu.postUpdate();
      window.requestAnimationFrame(update);
    };

    update();
}


  _initOcean() {
    // 8000×8000 / 256 segments = 31.25 m/segment — échelle 1:1 (1 unité = 1 mètre)
    const waterGeometry = new THREE.PlaneGeometry(8000, 8000, 256, 256);

    this._water = new Water(waterGeometry, {
      textureWidth: 256,   // résolution réduite du render target reflet
      textureHeight: 256,  // pour compenser le coût du render pass miroir
      waterNormals: new THREE.TextureLoader().load('textures/waternormals.jpg', (texture) => {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      }),
      sunDirection: new THREE.Vector3(0.3, 1.0, 0.3).normalize(),
      sunColor: 0xffffff,
      waterColor: 0x003d6b,
      distortionScale: 5.0,
      fog: false,
    });

    this._water.rotation.x = -Math.PI / 2;
    this._water.position.y = 0;  // Y=0 = niveau de la mer
    this._scene.add(this._water);
  }
  

  _initRaft() {
    const loader = new GLTFLoader();

    loader.load(
        '/models/wooden_raft.glb',
        (gltf) => {
            const raft = gltf.scene;

            raft.position.set(-6, 0.2, 0);  // 0.2m au-dessus du niveau de la mer (Y=0)
            raft.scale.set(7, 7, 7);

            this._scene.add(raft);
        },
        undefined,
        (error) => {
            console.error('Une erreur est survenue lors du chargement du radeau :', error);
        }
    );
  }

  _addBretonFlag() {
    const loader = new THREE.TextureLoader();
    const flagTexture = loader.load('textures/drapeau_breton.png');

    // Vertex shader : animation ondulante calculée entièrement sur GPU
    const flagVertexShader = `
      uniform float time;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        vec3 pos = position;
        float xNormalized = (pos.x + 2.5) / 5.0;
        if (xNormalized > 0.0) {
          pos.z = sin(0.5 * pos.x + 0.3 * pos.y - time) * xNormalized;
        }
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `;

    const flagFragmentShader = `
      uniform sampler2D map;
      varying vec2 vUv;
      void main() {
        gl_FragColor = texture2D(map, vUv);
      }
    `;

    const flagGeometry = new THREE.PlaneGeometry(5, 3, 30, 20);
    const flagMaterial = this._flagMaterial = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: flagTexture },
        time: { value: 0.0 },
      },
      vertexShader: flagVertexShader,
      fragmentShader: flagFragmentShader,
      side: THREE.DoubleSide,
    });

    const flag = new THREE.Mesh(flagGeometry, flagMaterial);
    flag.position.set(-8.6, 11.5, -4);  // +1m (mer passée de y=-1 à y=0)
    const yAxis = new THREE.Vector3(0, 1, 0);
    flag.rotateOnAxis(yAxis, Math.PI);

    const poleGeometry = new THREE.CylinderGeometry(0.1, 0.1, 28, 8);
    const poleMaterial = new THREE.MeshBasicMaterial({ color: 0x150C06 });
    const pole = new THREE.Mesh(poleGeometry, poleMaterial);
    pole.position.set(-6, -1, -4);  // +1m

    const flagOnPole = new THREE.Group();
    flagOnPole.add(pole);
    flagOnPole.add(flag);

    this._scene.add(flagOnPole);
  }

  _initPoineDuRaz() {
    // ── ATMOSPHÈRE — 1 unité = 1 mètre ─────────────────────────────────────────
    // FogExp2(0.00008) : 50% brume à ~8600m → horizon visible plus loin, repères lisibles
    this._scene.fog = new THREE.FogExp2(0xaaaaaa, 0.00008);
    this._scene.background = new THREE.Color(0xaaaaaa);  // évite l'horizon noir quand le terrain se termine

    const hemi = new THREE.HemisphereLight(0x98b4cc, 0x4a6030, 0.65);
    hemi.matrixAutoUpdate = false;
    this._scene.add(hemi);

    // Hash déterministe (reproduisible à chaque chargement)
    const h = (n) => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };

    // ── MATÉRIAUX ────────────────────────────────────────────────────────────
    const graniteWet = new THREE.MeshLambertMaterial({ color: 0x7a7470 });
    const whiteMat   = new THREE.MeshLambertMaterial({ color: 0xdedbd4 });
    const greyMat    = new THREE.MeshLambertMaterial({ color: 0x868482 });
    const roofMat    = new THREE.MeshLambertMaterial({ color: 0x1e1c1a });
    const redMat     = new THREE.MeshLambertMaterial({ color: 0xbb1111 });
    const lanternM   = new THREE.MeshBasicMaterial  ({ color: 0xffffa0 });
    const landesMat  = new THREE.MeshLambertMaterial({ color: 0x5a6a30 });
    const redRoof    = new THREE.MeshLambertMaterial({ color: 0x882222 });

    // Rocher IcosahedronGeometry déformé (pour le phare + rochers isolés)
    const makeRock = (sx, sy, sz, mat) => {
      const geo = new THREE.IcosahedronGeometry(1, 1);
      const pos = geo.attributes.position;
      for (let vi = 0; vi < pos.count; vi++) {
        const nx = pos.getX(vi), ny = pos.getY(vi), nz = pos.getZ(vi);
        const dd = 0.82 + Math.sin(nx * 8.1 + ny * 6.3) * 0.11
                       + Math.cos(nz * 9.7 + nx * 4.5) * 0.09;
        pos.setXYZ(vi, nx * sx * dd, ny * sy * dd, nz * sz * dd);
      }
      geo.computeVertexNormals();
      const mesh = new THREE.Mesh(geo, mat);
      mesh.matrixAutoUpdate = false;
      return mesh;
    };

    // ── TERRAIN 8×8 KM — displacement map fBm procédural ───────────────────────
    // PlaneGeometry(8000, 8000, 512, 512) = 15.6m/segment
    // UV.x=0 → X=-4000 (Ouest / Île de Sein)
    // UV.x=1 → X=+4000 (Est / Cap Sizun)
    const heightmap  = this._buildHeightmapTexture();
    const terrainGeo = new THREE.PlaneGeometry(8000, 8000, 512, 512);
    const terrainMat = new THREE.MeshStandardMaterial({
      displacementMap:   heightmap,
      displacementScale: 76,        // 76m réels — échelle 1:1
      displacementBias:  -1,        // pixels=0 → y=-1 (fond marin), pixels=1 → y=75m
      color: 0x2e352b,              // fallback : roche/landes sombres
      roughness: 0.92,
      metalness: 0.00,
    });

    // Orthophoto satellite IGN (Géoportail, domaine public) avec gestion CORS
    const satLoader = new THREE.TextureLoader();
    satLoader.setCrossOrigin('anonymous');
    satLoader.load(
      '/textures/pointe-du-raz/satellite.jpg',
      (tex) => { terrainMat.map = tex; terrainMat.needsUpdate = true; },
      undefined,
      () => { /* CORS échec — couleur fallback 0x2e352b déjà active */ },
    );

    const terrain = new THREE.Mesh(terrainGeo, terrainMat);
    terrain.rotation.x = -Math.PI / 2;
    terrain.position.set(0, 0, 0);
    terrain.receiveShadow = false;
    terrain.matrixAutoUpdate = false;
    terrain.updateMatrix();
    this._scene.add(terrain);

    // ── PHARE DE LA VIEILLE — redesign fidèle aux photos ────────────────────────
    // Tour granite brun, ~27m, sur rocher massif du Raz de Sein
    const lhGroup = new THREE.Group();
    lhGroup.position.set(220, 0, -25);

    const addPart = (geo, mat, px, py, pz) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(px, py, pz);
      m.matrixAutoUpdate = false; m.updateMatrix();
      lhGroup.add(m);
    };

    // Matériaux spécifiques (granite brun, métal sombre, verre chaud)
    const phStone  = new THREE.MeshLambertMaterial({ color: 0x6e5040 }); // granite brun-rouge
    const phStoneL = new THREE.MeshLambertMaterial({ color: 0x927866 }); // pierre claire (haut)
    const phIron   = new THREE.MeshLambertMaterial({ color: 0x252018 }); // métal sombre
    const phGlass  = new THREE.MeshBasicMaterial  ({ color: 0xfff0a0 }); // lueur lanterne

    // Rocher de la Vieille — massif et irrégulier
    const lhRock = makeRock(15, 5.5, 13, graniteWet);
    lhRock.position.set(0, 2.8, 0);
    lhRock.updateMatrix();
    lhGroup.add(lhRock);

    addPart(new THREE.CylinderGeometry(4.2, 6.2, 3.5, 12), graniteWet, 0,  6.8, 0); // pied évasé
    addPart(new THREE.CylinderGeometry(3.6, 4.2, 7.0, 12), phStone,   0, 12.3, 0); // corps bas brun foncé
    addPart(new THREE.CylinderGeometry(3.4, 3.6, 0.7, 12), phIron,    0, 16.2, 0); // liseré de rupture
    addPart(new THREE.CylinderGeometry(3.2, 3.4, 5.5, 12), phStone,   0, 19.6, 0); // corps milieu
    addPart(new THREE.CylinderGeometry(3.0, 3.2, 4.0, 12), phStoneL,  0, 24.8, 0); // corps haut clair
    addPart(new THREE.CylinderGeometry(4.0, 3.0, 0.8, 16), phIron,    0, 27.2, 0); // corbeau galerie
    addPart(new THREE.CylinderGeometry(4.0, 4.0, 0.3, 16), phIron,    0, 27.7, 0); // plancher galerie
    addPart(new THREE.CylinderGeometry(4.0, 4.0, 0.9, 16), phIron,    0, 28.2, 0); // garde-corps
    addPart(new THREE.CylinderGeometry(2.8, 2.8, 1.5, 10), phIron,    0, 29.5, 0); // salle de la machine
    addPart(new THREE.CylinderGeometry(2.7, 2.7, 3.5,  8), phIron,    0, 32.0, 0); // lanterne (octogonale)
    addPart(new THREE.CylinderGeometry(2.3, 2.3, 3.2,  8), phGlass,   0, 32.0, 0); // vitre (lueur)
    addPart(new THREE.ConeGeometry   (3.0, 2.8,  8),       phIron,    0, 35.0, 0); // dôme
    addPart(new THREE.CylinderGeometry(0.06,0.06,2.0,  4), phIron,    0, 36.9, 0); // mât
    addPart(new THREE.BoxGeometry(5.5, 3.2, 3.8),          phStone,  -4.2, 8.0, 0); // local technique

    lhGroup.matrixAutoUpdate = false; lhGroup.updateMatrix();
    this._scene.add(lhGroup);

    // ── TOURELLE DE LA PLATE — balise conique rouge ───────────────────────────
    const platGroup = new THREE.Group();
    platGroup.position.set(90, 0, 32);

    const addPlat = (geo, mat, px, py, pz) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(px, py, pz);
      m.matrixAutoUpdate = false; m.updateMatrix();
      platGroup.add(m);
    };

    const platRedMat = new THREE.MeshLambertMaterial({ color: 0xc42222 });
    const platTopMat = new THREE.MeshBasicMaterial  ({ color: 0xffffff });

    const platRock = makeRock(5.5, 2.2, 5.0, graniteWet);
    platRock.position.set(0, 1.1, 0);
    platRock.updateMatrix();
    platGroup.add(platRock);

    addPlat(new THREE.CylinderGeometry(2.0, 2.6, 2.0, 8), graniteWet, 0,  3.3, 0); // base maçonnerie
    addPlat(new THREE.CylinderGeometry(0.6, 2.0, 8.0, 8), platRedMat, 0,  8.3, 0); // corps conique rouge
    addPlat(new THREE.CylinderGeometry(0.5, 0.6, 0.8, 8), platRedMat, 0, 12.7, 0); // couronnement
    addPlat(new THREE.SphereGeometry(0.65, 8, 5),          platTopMat, 0, 13.7, 0); // voyant blanc

    platGroup.matrixAutoUpdate = false; platGroup.updateMatrix();
    this._scene.add(platGroup);

    // ── GORLE GREIZ — rochers affleurants à l'entrée du Raz ──────────────────
    const gGGroup = new THREE.Group();
    gGGroup.position.set(330, 0, 12);

    const gRock0 = makeRock(11, 4.0, 10, graniteWet);
    gRock0.position.set( 0, 2.0,  0); gRock0.updateMatrix(); gGGroup.add(gRock0);
    const gRock1 = makeRock( 6, 2.5,  5, graniteWet);
    gRock1.position.set(-9, 1.2,  7); gRock1.updateMatrix(); gGGroup.add(gRock1);
    const gRock2 = makeRock( 5, 1.8,  4, graniteWet);
    gRock2.position.set( 8, 1.0, -6); gRock2.updateMatrix(); gGGroup.add(gRock2);

    gGGroup.matrixAutoUpdate = false; gGGroup.updateMatrix();
    this._scene.add(gGGroup);

    // ── ÎLE DE SEIN — forme reconstituée d'après satellite IGN ──────────────
    // Île elongée E-O : lobe Ouest (landes), isthme central, village Est
    const seinGroup = new THREE.Group();
    seinGroup.position.set(-700, 0, 0);

    const addS = (geo, mat, px, py, pz, ry = 0) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(px, py, pz);
      m.rotation.y = ry;
      m.matrixAutoUpdate = false; m.updateMatrix();
      seinGroup.add(m);
    };

    const seinSandMat  = new THREE.MeshLambertMaterial({ color: 0xc8b882 }); // plages
    const seinHeathMat = new THREE.MeshLambertMaterial({ color: 0x6a7a40 }); // landes
    const seinVillMat  = new THREE.MeshLambertMaterial({ color: 0x8a8074 }); // village

    // Partie Ouest — lande (lobe principal + péninsules)
    addS(new THREE.BoxGeometry(180, 5, 130), seinHeathMat,  -90, 2.5,   0);
    addS(new THREE.BoxGeometry(120, 4, 100), seinHeathMat,  -80, 2.0, -42,  0.35); // péninsule NW
    addS(new THREE.BoxGeometry( 90, 4,  70), seinHeathMat, -130, 2.0,  34, -0.20); // péninsule SW
    addS(new THREE.BoxGeometry( 55, 3,  42), seinSandMat,   -50, 1.5,  52,  0.10); // plage S
    // Isthme central (Plas ar Skoul)
    addS(new THREE.BoxGeometry( 75, 3,  44), seinHeathMat,   12, 1.5,   0,  0.10);
    // Partie Est — zone village + port
    addS(new THREE.BoxGeometry(140, 5, 110), seinVillMat,   105, 2.5,   0, -0.10);
    addS(new THREE.BoxGeometry( 55, 3,  45), seinSandMat,    85, 1.5, -46,  0.15); // Kélaourou SE
    // Socle rocheux
    addS(new THREE.BoxGeometry(390, 2, 175), graniteWet,      0, -1.0,  0);

    // Village (maisons blanches regroupées à l'Est)
    for (let i = 0; i < 14; i++) {
      const bx =  60 + (h(i * 53    ) - 0.5) * 110;
      const bz =       (h(i * 53 + 1) - 0.5) *  80;
      addS(new THREE.BoxGeometry(6, 6, 6),  whiteMat, bx,  7.5, bz);
      addS(new THREE.ConeGeometry(5, 4, 4), redRoof,  bx, 13.0, bz);
    }

    // Phare de Goulenéz (pointe Est de l'île)
    addS(new THREE.CylinderGeometry(2.5, 3.5, 28, 8), whiteMat,  170, 17, -5);
    addS(new THREE.ConeGeometry   (4.0, 6.0,  8),     roofMat,   170, 33, -5);

    seinGroup.matrixAutoUpdate = false; seinGroup.updateMatrix();
    this._scene.add(seinGroup);
  }

  _buildHeightmapTexture() {
    // Génération procédurale fBm (Fractional Brownian Motion)
    // Coordonnées UV du terrain 8×8 km :
    //   U=0 → X=-4000m (Ouest / Île de Sein)    U=1 → X=+4000m (Est / Cap Sizun)
    //   V=0/1 → Z=±4000m (sud/nord)
    const SIZE = 512;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE; canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(SIZE, SIZE);
    const d = img.data;

    // Bruit de valeur lissé (smooth value noise, déterministe)
    const hn = (a, b) => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };
    const vnoise = (x, y) => {
      const ix = Math.floor(x), iy = Math.floor(y);
      const fx = x - ix, fy = y - iy;
      const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
      return hn(ix,iy)*(1-ux)*(1-uy) + hn(ix+1,iy)*ux*(1-uy)
           + hn(ix,iy+1)*(1-ux)*uy   + hn(ix+1,iy+1)*ux*uy;
    };
    // fBm lisse (7 octaves) pour les ondulations du plateau
    const fbm = (x, y) => {
      let v = 0, a = 0.5, f = 1;
      for (let i = 0; i < 7; i++) { v += a * vnoise(x * f, y * f); a *= 0.5; f *= 2.07; }
      return v;  // ≈0..1
    };
    // fBm turbulent (abs) pour les fractures/falaises/bords rocheux
    const fbmRock = (x, y) => {
      let v = 0, a = 0.5, f = 4;
      for (let i = 0; i < 5; i++) { v += a * Math.abs(vnoise(x*f, y*f) - 0.5) * 2; a *= 0.5; f *= 2.1; }
      return v;
    };

    for (let py = 0; py < SIZE; py++) {
      for (let px = 0; px < SIZE; px++) {
        const u = px / (SIZE - 1);  // 0=Ouest, 1=Est
        const v = py / (SIZE - 1);  // 0=Sud,   1=Nord
        let elev = 0;

        // ── ÎLE DE SEIN (Ouest) ──────────────────────────────────────────
        // X=-4000à-3000m → U=0..0.125 : île plate ~5m
        if (u < 0.18) {
          const t = Math.max(0, 1 - u / 0.10);  // 1 au bord ouest, 0 à U=0.10
          const noise = fbm(u * 8 + 1.7, v * 6 + 0.5);
          elev = t * t * 0.07 * (0.6 + 0.4 * noise);  // 5m max = 5/76 ≈ 0.066
          // Côte Est de l'île plus rocheuse
          if (u > 0.07 && u < 0.16) {
            elev += (1 - Math.abs(u - 0.11) / 0.05) * 0.04 * fbmRock(u * 20, v * 15 + 2.1);
          }
        }

        // ── HAUT-FONDS / ROCHERS DU RAZ ───────────────────────────────
        // U=0.10..0.69 : zone marine avec rochers affleurants
        if (u > 0.10 && u < 0.69) {
          const r = fbm(u * 50 + v * 40, v * 55 + u * 35 + 7.3);
          if (r > 0.87) elev = Math.max(elev, (r - 0.87) / 0.13 * 0.10);
        }

        // ── POINTE DU RAZ / CAP SIZUN (Est) ─────────────────────────────────
        // Ligne de côte variable en V — forme en C (Baie des Trépassés) :
        //   V=0.38 → Pointe du Raz (avance jusqu'à X≈400m)
        //   V=0.50 → Baie des Trépassés (recul à X≈1120m)
        //   V=0.62 → Pointe du Van (avance jusqu'à X≈560m)
        const razBell    = Math.exp(-Math.pow((v - 0.38) / 0.06, 2));
        const vanBell    = Math.exp(-Math.pow((v - 0.62) / 0.05, 2));
        const cliffEdgeU = 0.64 - razBell * 0.09 - vanBell * 0.07;
        // Porte côtière : falaise présente seulement entre V=0.20 et V=0.80
        const coastGate  = Math.max(0, Math.min(1, (v - 0.20) / 0.10))
                         * Math.max(0, Math.min(1, (0.80 - v) / 0.10));

        if (coastGate > 0 && u > cliffEdgeU - 0.04) {
          const cliffRaw = (u - cliffEdgeU) / 0.020;
          const ct = Math.max(0, Math.min(1, cliffRaw));
          const cliff = ct * ct * ct * (ct * (ct * 6 - 15) + 10); // quintic smoothstep

          const plateauH = 0.78 + 0.22 * fbm(u * 4 + v * 3, v * 5 + 1.1);

          const cliffFace = (u < cliffEdgeU + 0.08)
            ? fbmRock(u * 80 + v * 60, v * 90 + 4.4) * 0.12 : 0;

          const crestFactor = Math.max(0, 0.03 - Math.abs(u - (cliffEdgeU + 0.015))) / 0.03;
          const crest = crestFactor * fbmRock(u * 100 + v * 80, v * 120 + 3.7) * 0.2;

          elev = Math.min(1, Math.max(elev, coastGate * (cliff * plateauH + cliffFace + crest)));
        }

        const val = Math.round(elev * 255);
        const i = (py * SIZE + px) * 4;
        d[i] = d[i+1] = d[i+2] = val;
        d[i+3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    return new THREE.CanvasTexture(canvas);
  }



}

